import { useCallback, useEffect } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Card,
  CardContent,
  PageHeader,
  Spinner,
  Button,
  Tabs,
  TabList,
  TabTrigger,
  TabContent,
} from '@/components/ui';
import { MetricCard } from '@/components/metrics-guide/MetricCard';
import { useMetricDefinitions } from '@/hooks/use-metric-definition';
import { useAuth } from '@/contexts/AuthContext';
import type { IMetricDefinition } from '@skystone/shared';
import { PageContainer } from '@/components/layout';

// ---------------------------------------------------------------------------
// Static layout content (not user-editable — US6 does not expose these either)
// ---------------------------------------------------------------------------

const INTRO_HEADING = 'Understanding your P&E metrics';

const INTRO_BODY =
  'Skystone tracks three sets of metrics: Engineering metrics (from the DORA research programme) measure how effectively your team ships and operates software. Product metrics (from Agile Flow) measure how well work moves through delivery. Code Quality metrics (from SonarSource) measure the state of the codebase itself. Together they give a complete picture of team health — not just output, but quality, speed and reliability.';

const ENGINEERING_SECTION_SOURCE = 'Source: DORA State of DevOps Research';
const PRODUCT_SECTION_SOURCE = 'Based on Agile Flow · Lean/Kanban & Scrum research';
const CODE_QUALITY_SECTION_SOURCE = 'Source: SonarSource (SonarQube/SonarCloud)';
const GOVERNANCE_SECTION_SOURCE = 'Source: Engineering standards (org-wide)';

// ---------------------------------------------------------------------------
// Tab handling
// ---------------------------------------------------------------------------

type MetricCatalogueTab = 'engineering' | 'product' | 'code-quality' | 'governance';

const VALID_TABS: ReadonlySet<MetricCatalogueTab> = new Set<MetricCatalogueTab>([
  'engineering',
  'product',
  'code-quality',
  'governance',
]);

const DEFAULT_TAB: MetricCatalogueTab = 'engineering';

function parseTab(value: string | null): MetricCatalogueTab {
  if (value && VALID_TABS.has(value as MetricCatalogueTab)) {
    return value as MetricCatalogueTab;
  }
  return DEFAULT_TAB;
}

// ---------------------------------------------------------------------------
// Group helpers
// ---------------------------------------------------------------------------

function metricsByGroup(definitions: readonly IMetricDefinition[], groupKey: IMetricDefinition['groupKey']): IMetricDefinition[] {
  return definitions
    .filter((m) => m.groupKey === groupKey)
    .sort((a, b) => a.displayOrder - b.displayOrder);
}

// ---------------------------------------------------------------------------
// Subcomponents (module-scoped — S7721)
// ---------------------------------------------------------------------------

function IntroBand() {
  return (
    <Card>
      <CardContent className="p-4">
        <h2 className="mb-1 text-sm font-bold text-foreground">{INTRO_HEADING}</h2>
        <p className="text-sm leading-relaxed text-muted-foreground">{INTRO_BODY}</p>
      </CardContent>
    </Card>
  );
}

function MetricGrid({ metrics }: Readonly<{ metrics: IMetricDefinition[] }>) {
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
      {metrics.map((metric) => (
        <div key={metric.metricKey} id={metric.metricKey} className="h-full">
          <MetricCard definition={metric} />
        </div>
      ))}
    </div>
  );
}

function TabPanelHeader({ source }: Readonly<{ source: string }>) {
  return <p className="text-xs text-muted-foreground">{source}</p>;
}

// ---------------------------------------------------------------------------
// MetricCataloguePage
// ---------------------------------------------------------------------------

export function MetricCataloguePage() {
  const { definitions, isLoading } = useMetricDefinitions();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAdmin = user?.role === 'SiteAdmin' || user?.role === 'DataQualityAdmin';
  const engineering = metricsByGroup(definitions, 'engineering');
  const product = metricsByGroup(definitions, 'product');
  const codeQuality = metricsByGroup(definitions, 'code_quality');
  const governance = metricsByGroup(definitions, 'governance');

  // Fall back to the default tab if the URL requests a tab whose group is
  // empty (e.g. ?tab=code-quality on a tenant without code-quality metrics).
  const requestedTab = parseTab(searchParams.get('tab'));
  let activeTab = requestedTab;
  if (requestedTab === 'code-quality' && codeQuality.length === 0) activeTab = DEFAULT_TAB;
  if (requestedTab === 'governance' && governance.length === 0) activeTab = DEFAULT_TAB;

  const handleTabChange = useCallback((next: string) => {
    const tab = parseTab(next);
    const params = new URLSearchParams(searchParams);
    if (tab === DEFAULT_TAB) {
      params.delete('tab');
    } else {
      params.set('tab', tab);
    }
    // Preserve the hash so deep links like /metric-catalogue?tab=product#cq_coverage
    // can auto-switch tabs without losing the anchor target.
    navigate({ search: params.toString(), hash: location.hash }, { replace: true });
  }, [navigate, searchParams, location.hash]);

  // Scroll to hash-targeted metric card and briefly highlight it. The hash
  // anchor only resolves once its tab is active, so we switch tabs first if
  // the hash points at a metric in a non-active group.
  useEffect(() => {
    if (isLoading || definitions.length === 0) return;
    const hash = location.hash.replace('#', '');
    if (!hash) return;
    const targetMetric = definitions.find((m) => m.metricKey === hash);
    if (!targetMetric) return;

    const groupToTab: Record<IMetricDefinition['groupKey'], MetricCatalogueTab> = {
      engineering: 'engineering',
      product: 'product',
      code_quality: 'code-quality',
      governance: 'governance',
    };
    const targetTab = groupToTab[targetMetric.groupKey];
    if (targetTab !== activeTab) {
      handleTabChange(targetTab);
      return;
    }

    const element = document.getElementById(hash);
    if (!element) return;
    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    element.classList.add('ring-2', 'ring-primary', 'rounded-lg');
    const timer = setTimeout(() => {
      element.classList.remove('ring-2', 'ring-primary', 'rounded-lg');
    }, 2000);
    return () => clearTimeout(timer);
  }, [isLoading, definitions, location.hash, activeTab, handleTabChange]);

  if (isLoading) {
    return (
      <PageContainer variant="wide" className="space-y-6 p-6">
        <PageHeader title="Definitions Catalogue" subtitle="Understand what each dashboard metric means and how to use it." />
        <div className="flex items-center justify-center py-12"><Spinner /></div>
      </PageContainer>
    );
  }

  return (
    <PageContainer variant="wide" className="space-y-6 p-6">
      <PageHeader
        title="Definitions Catalogue"
        subtitle="Understand what each dashboard metric means and how to use it."
        action={isAdmin ? (
          <Link to="/admin/metric-definitions">
            <Button variant="ghost" size="sm">Edit definitions</Button>
          </Link>
        ) : undefined}
      />

      <IntroBand />

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabList>
          <TabTrigger value="engineering">Engineering (DORA)</TabTrigger>
          <TabTrigger value="product">Product (Agile Flow)</TabTrigger>
          {codeQuality.length > 0 && (
            <TabTrigger value="code-quality">Code Quality</TabTrigger>
          )}
          {governance.length > 0 && (
            <TabTrigger value="governance">Workflow Hygiene</TabTrigger>
          )}
        </TabList>

        <TabContent value="engineering" className="space-y-3">
          <TabPanelHeader source={ENGINEERING_SECTION_SOURCE} />
          <MetricGrid metrics={engineering} />
        </TabContent>

        <TabContent value="product" className="space-y-3">
          <TabPanelHeader source={PRODUCT_SECTION_SOURCE} />
          <MetricGrid metrics={product} />
        </TabContent>

        {codeQuality.length > 0 && (
          <TabContent value="code-quality" className="space-y-3">
            <TabPanelHeader source={CODE_QUALITY_SECTION_SOURCE} />
            <MetricGrid metrics={codeQuality} />
          </TabContent>
        )}

        {governance.length > 0 && (
          <TabContent value="governance" className="space-y-3">
            <TabPanelHeader source={GOVERNANCE_SECTION_SOURCE} />
            <MetricGrid metrics={governance} />
          </TabContent>
        )}
      </Tabs>
    </PageContainer>
  );
}
